import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import { Box, Button, Drawer, ListItem, ListItemButton, ListItemText, Skeleton, Typography, createTheme } from "@mui/material";
import List from "@mui/material/List";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import appConfig from '../utils/Config';
import axiosConfig from "../utils/axiosConfig";
import HTMLContent from "./HTMLContent";
import ListItemLinks from "./ListItemLinks";
import SearchBar from "./SearchBar";
import SearchResults from "./SearchResults";
import './styles.css';

const theme = createTheme()
const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
  },
  contentShift: {
    marginLeft: 0,
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
  },

}))


const ManualBrowser = () => {
  const classes = useStyles()
  const [data, setData] = useState([]);
  const [keyword, setKeyword] = useState(null)
  const [searchParams] = useSearchParams()
  const [contentType, setContentType] = useState('')
  const [loading, setLoading] = useState(true)
  const [pageTitle, setPageTitle] = useState('')
  const searchBar = useRef(null)

  const navigate = useNavigate()


  const pageId = searchParams.get('page')

  const location = useLocation()
  let { manual } = useParams()
  if (!manual) {
    manual = location.state
  }

  const url = appConfig.drupal.host

  const fetchPage = async () => {
    const axios = await axiosConfig();
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/manual_pages`, {
        params: {
          pageId: pageId
        }
      })

      if (response.data) {

        setData(response.data)
        setContentType('page')
        setKeyword('')

        if (window.location.hash != '') {
          window.locateAnchor = setTimeout(() => animateToAnchor(window.location.hash), 50)
        }

        if (pageTitle == '') {
          // GET MANUAL NAME
          try {
            const response = await axios.get(`${url}/api/manual/${manual}`)

            if (response.data) {
              setPageTitle(response.data.title)
            }

          } catch (e) {
            console.error(e.text)
          }
        }


      }
    } catch (e) {
      console.error(e)
    }
    setLoading(false)

  }

  useEffect(() => {
    if (window.location.hash != '') {
      window.locateAnchor = setTimeout(() => animateToAnchor(window.location.hash), 50)
    }
  }, [window.location.hash])

  useEffect(() => {
    if (pageTitle != '') {
      document.title = pageTitle + ' |' + document.title.split('|')[1]
    }
    
  }, [pageTitle])



  const fetchGuides = async () => {
    const axios = await axiosConfig();
    setLoading(true)
    try {
      const response = await axios.get(`${url}/api/manual/${manual}`)

      if (response.data) {
        setData(response.data)
        setContentType('guide')
        setKeyword('')
        setPageTitle(response.data.title)
      }

    } catch (e) {
      console.error(e.text)
    }
    setLoading(false)

  }
  useEffect(() => {
    if (pageId) {
      fetchPage()
    } else {
      fetchGuides()
    }

  }, [manual, pageId, url]);


  const handleSearch = (key) => {
    setKeyword(key)
    searchBar.current.setSearchValue(key)
  }
  const animateToAnchor = (hash) => {
    var anchor = document.getElementById(hash.replace('#', ''))
    if (anchor) {
      const header = document.getElementsByClassName('l-header')
      const isNavReduced = window.pageYOffset > 100


      const yOffset = header[0].offsetHeight



      let y = anchor.getBoundingClientRect().top + window.pageYOffset - yOffset;

      if (isNavReduced) {
        if (y < 100) {
          y -= 61
        }
      } else {
        if (y > 100) {
          y += 61
        }
      }
      window.scrollTo({ top: y, behavior: 'smooth' })
    }
    if (window.locateAnchor) {
      clearInterval(window.locateAnchor)
    }
  }

  const hashSelected = (to) => {
    navigate(`${window.location.search}${to}`, { state: manual })
  }

  const nestedList = (elements, depth) => {
    if (elements.length === 0)
      return

    return (<List key={depth} data-depth={depth}>
      {
        elements.map((page) => {
          let to = `/manual/${manual}?page=${page.id}`
          if (contentType === 'page')
            to = `#bookmark_${page.id}`

          const selected = to === location.hash || (pageId === page.id && location.hash === '')

          return (
            <>
              <ListItem key={page.id}>
                <ListItemLinks
                  depth={page.depth}
                  title={page.title}
                  isMain={contentType === 'guide'}
                  to={to}
                  manualId={manual}
                  onSelected={hashSelected}
                  isSelected={selected}
                />
              </ListItem>
              {page.children ? nestedList(page.children, depth + 1) : null}
            </>
          )
        })
      }
    </List>)
  }

  const backToTop = (event) => {
    event.preventDefault()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className={classes.root}>
      <Drawer
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            bgcolor: '#f0f7fc',
            position: 'relative'
          },
        }}
        variant="permanent"
        anchor="left"
        className="manual-drawer"
      >
        <nav className={`${classes.columnFlex} ${classes.applyDrawerBorder}`}>
          <div className="search-bar">
            <SearchBar ref={searchBar} label="Index Search" handleSearch={handleSearch} keyword={keyword} />
          </div>
          {contentType === 'page' ?
            <div className="deep-navigation">
              <Button
                startIcon={<FormatListBulletedIcon />}
                onClick={() => navigate(`/manual/${manual}`)}>
                Index
              </Button>
              <div className="prev-next">
                {data.prev_page ? (
                  <Button
                    startIcon={<ArrowBackIosOutlinedIcon />}
                    onClick={() => navigate(`/manual/${manual}/?page=${data.prev_page}`)}>
                    Previous
                  </Button>
                ) : null}
                {data.next_page ? (
                  <Button
                    endIcon={<ArrowForwardIosOutlinedIcon />}
                    onClick={() => navigate(`/manual/${manual}/?page=${data.next_page}`)}>
                    Next
                  </Button>
                ) : null
                }
              </div>
            </div> : null}
          {data.index ?
            <div dangerouslySetInnerHTML={{ __html: `<style>body.path-manual .l-page .l-content .l-content__inner .MuiList-root[data-depth="1"] { counter-reset: count ${data.index - 1} } </style>` }} />
            : null}

          {loading ? <List>
            <ListItem>
              <ListItemButton>
                <ListItemText>
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" width="60%" />
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton>
                <ListItemText>
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" width="60%" />
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton>
                <ListItemText>
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" width="60%" />
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton>
                <ListItemText>
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" width="60%" />
                </ListItemText>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton>
                <ListItemText>
                  <Skeleton animation="wave" />
                  <Skeleton animation="wave" width="60%" />
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </List> : (
            data.table_of_contents ? nestedList(data.table_of_contents, 1) : null
          )}
          {contentType === 'page' ?
            <div className="deep-navigation nav-bottom">
              <Button
                startIcon={<FormatListBulletedIcon />}
                onClick={() => navigate(`/manual/${manual}`)}>
                Index
              </Button>
              <div className="prev-next">
                {data.prev_page ? (
                  <Button
                    startIcon={<ArrowBackIosOutlinedIcon />}
                    onClick={() => navigate(`/manual/${manual}/?page=${data.prev_page}`)}>
                    Previous
                  </Button>
                ) : null}
                {data.next_page ? (
                  <Button
                    endIcon={<ArrowForwardIosOutlinedIcon />}
                    onClick={() => navigate(`/manual/${manual}/?page=${data.next_page}`)}>
                    Next
                  </Button>
                ) : null
                }
              </div>
            </div> : null}
        </nav>
      </Drawer>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3 }}
        className="manual-content fixed-module"
      >
        {loading ? <>
          <Typography variant="h1">{loading ? <Skeleton /> : 'h1'}</Typography>
          <Typography variant="h2">{loading ? <Skeleton width="80%" /> : 'h2'}</Typography>
          <Typography variant="h3">{loading ? <Skeleton width="60%" /> : 'h3'}</Typography>
          <Skeleton animation="wave" height={10} style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="60%" style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" style={{ marginBottom: 20 }} />
          <Skeleton animation="wave" height={10} style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="60%" style={{ marginBottom: 6 }} />
          <Skeleton animation="wave" height={10} width="80%" style={{ marginBottom: 20 }} />


        </> : (
          !keyword ? (
            <HTMLContent
              body={data.content}
              title={data.title}
              isMain={contentType === 'guide'}
              toc={data.table_of_contents ? data.table_of_contents : []}
              key={data.id}
            />
          ) :
            (
              <SearchResults
                keyword={keyword}
                manualId={manual}
                handleSearch={handleSearch}
              />
            )

        )}

        {!loading ? <div className="back-to-top-wrapper">
          <div className="back-to-top">
            <a href="#" onClick={(e) => backToTop(e)} className="scroll-top" >
              <div className="label-content">
                <KeyboardArrowUpIcon />
                <span>Back to Top</span>
              </div>
            </a>
          </div>
        </div> : null}
      </Box>

    </div>
  )
}

export default ManualBrowser;
